<script lang="ts">
  import { userStore } from '$lib/stores/userStore.js';
  import Button from '$lib/components/control/Button.svelte';
  import {
    BUTTON_SIZE,
    BUTTON_VARIANT,
    OPT_IN_MARKETING_COUNTRIES,
  } from '$lib/constants/constants.js';
  import Facebook from '../../../assets/svgs/facebook.svelte';
  import ExternalLink from '../../../assets/svgs/external-link.svelte';
  import SlidingModal from '$lib/components/website/SlidingModal.svelte';
  import Checkbox from '$lib/components/control/Checkbox.svelte';
  import SignupForm from './SignupForm.svelte';
  import { Auth0ConnectionEnum, getLoginUrl, getLogoutUrl } from '$lib/helpers/auth0Helpers';
  import { goto } from '$app/navigation';
  import { createEventDispatcher } from 'svelte';
  import { createAccount } from '$lib/gateway/plausible';

  export let form;
  export let country: string | undefined = undefined;

  const dispatch = createEventDispatcher();

  // User state
  $: loading = $userStore.loading;
  $: user = $userStore.user;

  // Urls
  let loginUrl = getLoginUrl();
  let logoutUrl = getLogoutUrl();

  // Modal state
  let showSignupModal = false;
  let showFacebookModal = false;

  // Accept terms
  let acceptTerms = false;

  $: isOptInMarketingCountry = OPT_IN_MARKETING_COUNTRIES.find(
    (optInCountry) => optInCountry === country,
  );
</script>

<div>
  <p class="text-sm font-bold mb-4">2. Create an account</p>

  {#if loading}
    <div class="h-24 rounded-sm bg-gray-200 animate-pulse" />
  {:else if user}
    <div class="flex items-center justify-between gap-4 px-4 py-6 bg-gray-100">
      <div class="flex items-center gap-4">
        <img src={user.picture} alt={user.name} class="w-6 h-6 rounded-full" />
        <div>
          <p class="text-sm font-medium">{user.name}</p>
        </div>
      </div>

      <a href={logoutUrl} data-login="change-login" class="text-primary-500 text-sm font-medium"
        >Change</a
      >
    </div>
  {:else}
    <div>
      <div class="flex flex-col xl:flex-row gap-2">
        <Button
          dataTestId="sign-up-with-email"
          text="Sign up with email"
          variant={BUTTON_VARIANT.PRIMARY}
          buttonSize={BUTTON_SIZE.LARGE}
          customClass="flex-1"
          plausibleClass="email_signup"
          onClick={() => {
            showSignupModal = true;
          }}
        />

        <Button
          text="Continue with Facebook"
          variant={BUTTON_VARIANT.FACEBOOK}
          buttonSize={BUTTON_SIZE.LARGE}
          customClass="flex-1"
          plausibleClass="facebook_signup"
          onClick={() => {
            showFacebookModal = true;
          }}
        >
          <div slot="left">
            <Facebook customClass="w-6 h-6 text-white" />
          </div>
        </Button>
      </div>

      <span
        class="flex items-center justify-center gap-1 text-sweat-gray-40 text-center text-sm mt-4"
      >
        Already have an account?
        <a class="flex items-center gap-1 w-fit underline" href={loginUrl}>
          <span>Login</span>
          <ExternalLink />
        </a>
      </span>
    </div>
  {/if}

  <!-- Signup Modal -->
  <SlidingModal bind:showModal={showSignupModal}>
    <SignupForm
      {form}
      {country}
      onCompleted={(user) => {
        showSignupModal = false;
        dispatch('completed', user);
      }}
      onLogin={() => loginUrl && goto(loginUrl)}
    />
  </SlidingModal>

  <!-- Facebook Modal -->
  <SlidingModal bind:showModal={showFacebookModal}>
    <h2 class="text-md md:text-xl font-bold mb-2">Continue with Facebook</h2>
    <p class="text-md">Just one more thing before you sign up.</p>

    <div class="bg-sweat-gray-5 h-[1px] w-full my-6" />
    <div class="my-4">
      {#if !isOptInMarketingCountry}
        <Checkbox
          plausibleClass="newsletters"
          theme="black"
          size="lg"
          label="Yes, I want to receive news, updates and discounts from Sweat."
        />
      {/if}
      <Checkbox
        dataTestId="accept-terms"
        plausibleClass="terms"
        theme="black"
        size="lg"
        bind:checked={acceptTerms}
      >
        By continuing you accept our <a
          class="font-semibold underline"
          href="https://sweat.com/privacy-policy"
          target="_blank">Privacy Policy</a
        >
        and
        <a class="font-semibold underline" href="https://sweat.com/terms-of-use" target="_blank"
          >Terms of Use</a
        >.
      </Checkbox>
    </div>

    <Button
      text="Continue with Facebook"
      variant={BUTTON_VARIANT.FACEBOOK}
      buttonSize={BUTTON_SIZE.LARGE}
      customClass="w-full mt-10"
      plausibleClass="facebook_account"
      onClick={() => {
        const url = getLoginUrl({ connection: Auth0ConnectionEnum.Facebook });
        if (url) goto(url);
        createAccount();
      }}
      disabled={!acceptTerms}
    >
      <div slot="left">
        <Facebook customClass="w-6 h-6 text-white" />
      </div>
    </Button>
  </SlidingModal>
</div>
